import { track } from '@/config/mixpanel';
import { getPortalRuntimeConfig } from '@/services/runtimeConfig';

const reloadTimestampKey = 'stale_chunk_reload_timestamp';
const reloadCooldownMs = 30_000;
let reloadPending = false;

export function isReloadPending(): boolean {
  return reloadPending;
}

export function resetReloadPending(): void {
  reloadPending = false;
}

type StaleChunkRecoveryOptions = {
  clock?: () => number;
  storage?: Pick<Storage, 'getItem' | 'setItem'>;
  reload?: () => void;
  tracker?: (event: string, data: { asset_file_name?: string; version: string }) => void;
};

function getErrorMessage(error: unknown): string {
  if (typeof error === 'string') return error;
  if (error && typeof error === 'object' && 'message' in error) {
    return typeof error.message === 'string' ? error.message : '';
  }
  return '';
}

export function isStaleChunkError(error: unknown): boolean {
  const message = getErrorMessage(error);
  return (
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('error loading dynamically imported module') ||
    message.includes('Importing a module script failed') ||
    message.includes('Unable to preload CSS') ||
    message.includes('is not a valid JavaScript MIME type')
  );
}

export function attemptStaleChunkRecovery(
  error: unknown,
  {
    clock = Date.now,
    storage,
    reload = () => window.location.reload(),
    tracker = track,
  }: StaleChunkRecoveryOptions = {},
): boolean {
  if (!isStaleChunkError(error)) return false;
  if (isReloadPending()) return true;

  const nowMs = clock();
  try {
    const sessionStorage = storage ?? window.sessionStorage;
    const storedTimestamp = sessionStorage.getItem(reloadTimestampKey);
    if (storedTimestamp !== null) {
      const lastReloadMs = Number(storedTimestamp);
      if (Number.isFinite(lastReloadMs) && nowMs - lastReloadMs < reloadCooldownMs) return false;
    }
    sessionStorage.setItem(reloadTimestampKey, String(nowMs));
  } catch {
    return false;
  }

  const assetFileName = getErrorMessage(error).match(
    /\/([\w-]+\.(?:m?js|css))(?=[?#\s'"):]|$)/,
  )?.[1];
  reloadPending = true;
  tracker('auto_reload: stale_chunk', {
    ...(assetFileName ? { asset_file_name: assetFileName } : {}),
    version: getPortalRuntimeConfig().appVersion,
  });
  reload();
  return true;
}

export function installStaleChunkRecovery(options: StaleChunkRecoveryOptions = {}): () => void {
  const onPreloadError = (event: Event) => {
    if (
      isReloadPending() ||
      ('payload' in event && attemptStaleChunkRecovery(event.payload, options))
    ) {
      event.preventDefault();
    }
  };

  window.addEventListener('vite:preloadError', onPreloadError);
  return () => window.removeEventListener('vite:preloadError', onPreloadError);
}
